// src/index.js
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import CssBaseline from '@mui/material/CssBaseline';
import reportWebVitals from './reportWebVitals';

// Prevent browser from restoring scroll on reload
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Optional: create a wrapper to scroll to top on initial load
const Root = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return <App />;
};

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline /> {/* resets CSS and applies MUI theme */}
    <Root />
  </ThemeProvider>
);

// ============================================
// SERVICE WORKER REGISTRATION FOR PWA
// ============================================

// Register Service Worker for PWA support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
    
    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        console.log('✅ Service Worker registered successfully:', registration);
        
        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('🔄 New service worker found, installing...');
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              console.log('📦 New version available! Please refresh.');
              
              // Show update notification to user
              const updateEvent = new CustomEvent('pwa-update-available', {
                detail: { registration }
              });
              window.dispatchEvent(updateEvent);
            }
          });
        });
      })
      .catch((error) => {
        console.error('❌ Service Worker registration failed:', error);
      });
  });
}

// ============================================
// HANDLE OFFLINE / ONLINE STATUS
// ============================================

// Show offline notification when user goes offline
window.addEventListener('online', () => {
  console.log('🟢 Back online!');
  // Dispatch custom event for components to listen
  window.dispatchEvent(new CustomEvent('network-status', { detail: { online: true } }));
});

window.addEventListener('offline', () => {
  console.log('🔴 You are offline');
  window.dispatchEvent(new CustomEvent('network-status', { detail: { online: false } }));
});

// ============================================
// HANDLE PWA INSTALLATION
// ============================================

// Track if app is installed
window.addEventListener('appinstalled', () => {
  console.log('📱 App installed successfully!');
  window.dispatchEvent(new CustomEvent('pwa-installed'));
});

// ============================================
// REPORT WEB VITALS (Optional)
// ============================================

// If you want to measure performance in your app,
// pass a function to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log);

// ============================================
// REGISTER FOR PUSH NOTIFICATIONS (Optional)
// ============================================

// Example function to request push notification permission
const requestPushNotificationPermission = async () => {
  try {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('🔔 Push notification permission granted');
        
        // Get the service worker registration
        const registration = await navigator.serviceWorker.ready;
        
        // Subscribe to push notifications
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
        });
        
        console.log('📨 Push subscription:', subscription);
        
        // Send subscription to your server
        // await fetch('/api/save-push-subscription', {
        //   method: 'POST',
        //   body: JSON.stringify(subscription),
        //   headers: { 'Content-Type': 'application/json' }
        // });
      }
    }
  } catch (error) {
    console.error('Failed to request push notification permission:', error);
  }
};

// Uncomment to request push notification permission on load
// requestPushNotificationPermission();

// ============================================
// CUSTOM OFFLINE UI COMPONENT (Optional)
// ============================================

// You can add this to your App.js or main layout
// to show a banner when offline
/*
const OfflineBanner = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  useEffect(() => {
    const handleStatusChange = (e) => {
      setIsOffline(!e.detail.online);
    };
    
    window.addEventListener('network-status', handleStatusChange);
    return () => window.removeEventListener('network-status', handleStatusChange);
  }, []);
  
  if (!isOffline) return null;
  
  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: '#ff4757',
      color: 'white',
      padding: '12px 20px',
      textAlign: 'center',
      zIndex: 9999,
      fontWeight: 500
    }}>
      <span>📶 You are offline. Please check your internet connection.</span>
    </div>
  );
};
*/